import React, { useLayoutEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { CollectionHero, Container, theme, useResponsive } from '@sg/garnish';

import {
  MenuCollectionsRail,
  NotFoundView,
  PageWrapper,
} from '@order/components';
import {
  useResetLastInteractedMenuStore,
  useSetLastInteractedMenuStore,
} from '@order/LastInteractedStore';
import { useLocalizationContext } from '@order/Localization';

import type {
  DeliveryMenuCollectionScreenProps,
  MenuCollectionScreenProps,
} from '../../../../navigation/AppNavigation.props';
import { useMenuCollectionQuery } from '../../GraphQL';
import { MenuCollectionProductsGrid } from '../MenuCollectionProductsGrid';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuCollectionScreenContent = (props: ScreenProps) => {
  const { navigation, route, testID = 'collection-page-wrapper' } = props;

  const { params } = route;
  const { menuCollectionId, addressId, restaurantSlug } = params;

  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const resetLastInteractedMenuStore = useResetLastInteractedMenuStore();

  // ─── Data ────────────────────────────────────────────────────────

  const [{ data, fetching: isLoading, error }] = useMenuCollectionQuery({
    variables: { collectionId: menuCollectionId },
    pause: !menuCollectionId,
  });

  const { menuCollection } = data ?? {};
  const menuCollectionTitle = menuCollection?.primaryTitle ?? '';

  const {
    id = '',
    color = '',
    primaryTitle = '',
    primaryDescription = '',
    primaryImageUrl = '',
    products = [],
  } = menuCollection ?? {};

  // ─── Effects ─────────────────────────────────────────────────────

  useLayoutEffect(() => {
    navigation.setOptions({ headerTitle: menuCollectionTitle });
  }, [navigation, menuCollectionTitle]);

  useSetLastInteractedMenuStore({ restaurantSlug, addressId });

  // ─── Styles ──────────────────────────────────────────────────────

  const menuCollectionsRailWrapperStyle = [
    styles.menuCollectionsWrapper,
    currentBreakpoint.isXS && styles.menuCollectionsWrapperXS,
  ];

  // ─────────────────────────────────────────────────────────────────

  if (error) return <NotFoundView onMount={resetLastInteractedMenuStore} />;

  return (
    <PageWrapper contentContainerStyle={styles.wrapper} testID={testID}>
      <View>
        <CollectionHero
          backgroundColor={color}
          heading={primaryTitle}
          description={primaryDescription}
          imgUrl={primaryImageUrl}
          collectionsLabel={t('menu-collection.collections')}
          isLoading={isLoading}
          testID="collection-hero"
        />

        <MenuCollectionProductsGrid products={products} isLoading={isLoading} />

        <Container style={menuCollectionsRailWrapperStyle}>
          <MenuCollectionsRail
            menuCollectionId={id}
            isLoading={isLoading}
            headerText={t('menu-collection.more-collections')}
            excludedCollectionId={menuCollectionId}
            testID="more-collections-rail"
          />
        </Container>
      </View>
    </PageWrapper>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: theme.colors.KHAKI_3,
  },
  menuCollectionsWrapper: {
    marginBottom: theme.spacing['6'],
    paddingBottom: theme.spacing['10'],
  },
  menuCollectionsWrapperXS: {
    paddingTop: theme.spacing['10'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type ScreenProps = (
  | MenuCollectionScreenProps
  | DeliveryMenuCollectionScreenProps
) &
  Pick<ViewProps, 'testID'>;
